import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Link,
  Grid,
  Divider,
  Chip,
  Tooltip,
  Stack
} from '@mui/material';
import { format } from 'date-fns';
import { installBaseService } from '../../../services/installBase.service';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';

const LocationDetailsDialog = ({ open, onClose, locationData }) => {
  const navigate = useNavigate();
  const [locationDetails, setLocationDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [installationAlerts, setInstallationAlerts] = useState({});

  useEffect(() => {
    const fetchLocationDetails = async () => {
      if (!locationData || !locationData.location_id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await installBaseService.getLocationDetails(locationData.location_id);
        if (response && response.length > 0) {
          setLocationDetails(response[0]);
          // Fetch alerts for each installation
          const alertsPromises = response[0].installations_json.map(async (installation) => {
            try {
              const alerts = await installBaseService.getAlertsByInstallation(installation.installation_id);
              return { installationId: installation.installation_id, alerts };
            } catch (err) {
              console.error(`Error fetching alerts for installation ${installation.installation_id}:`, err);
              return { installationId: installation.installation_id, alerts: [] };
            }
          });
          
          const alertsResults = await Promise.all(alertsPromises);
          const alertsMap = alertsResults.reduce((acc, { installationId, alerts }) => {
            acc[installationId] = alerts;
            return acc;
          }, {});
          setInstallationAlerts(alertsMap);
        } else {
          setError('No location details found');
        }
      } catch (err) {
        console.error('Error fetching location details:', err);
        setError('Failed to load location details');
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchLocationDetails();
    }
  }, [open, locationData]);

  const handleClose = () => {
    onClose();
  };

  const getAlertChipColor = (alerts) => {
    if (!alerts || alerts.length === 0) return 'default';
    const hasHighSeverity = alerts.some(alert => alert.severity === 'high');
    const hasMediumSeverity = alerts.some(alert => alert.severity === 'medium');
    return hasHighSeverity ? 'error' : hasMediumSeverity ? 'warning' : 'info';
  };

  const getAlertSummary = (alerts) => {
    if (!alerts || alerts.length === 0) return { high: 0, medium: 0, low: 0 };
    return alerts.reduce((acc, alert) => {
      acc[alert.severity] = (acc[alert.severity] || 0) + 1;
      return acc;
    }, { high: 0, medium: 0, low: 0 });
  };

  const getAlertTooltip = (alerts) => {
    if (!alerts || alerts.length === 0) return 'No active alerts';
    
    const summary = getAlertSummary(alerts);
    const parts = [];
    if (summary.high > 0) parts.push(`${summary.high} high severity`);
    if (summary.medium > 0) parts.push(`${summary.medium} medium severity`);
    if (summary.low > 0) parts.push(`${summary.low} low severity`);
    
    return parts.join(', ');
  };

  const getLocationAlertSummary = () => {
    if (!locationDetails || !locationDetails.installations_json) return { high: 0, medium: 0, low: 0 };
    
    return locationDetails.installations_json.reduce((acc, installation) => {
      const alerts = installationAlerts[installation.installation_id] || [];
      const summary = getAlertSummary(alerts);
      acc.high += summary.high;
      acc.medium += summary.medium;
      acc.low += summary.low;
      return acc;
    }, { high: 0, medium: 0, low: 0 });
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
        <DialogContent>
          <Alert severity="error">{error}</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  if (!locationDetails) {
    return null;
  }

  const locationAlertSummary = getLocationAlertSummary();
  const totalAlerts = locationAlertSummary.high + locationAlertSummary.medium + locationAlertSummary.low;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div">
          <Link
            component="button"
            variant="h5"
            onClick={() => navigate(`/locations/${locationDetails.location_id}`)}
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {locationDetails.location_name}
          </Link>
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {locationDetails.customer_name}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Alert Summary Section */}
          {totalAlerts > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="h6" gutterBottom>
                  Active Alerts Summary
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  {locationAlertSummary.high > 0 && (
                    <Chip
                      icon={<ErrorIcon />}
                      label={`${locationAlertSummary.high} High`}
                      color="error"
                      size="small"
                    />
                  )}
                  {locationAlertSummary.medium > 0 && (
                    <Chip
                      icon={<WarningIcon />}
                      label={`${locationAlertSummary.medium} Medium`}
                      color="warning"
                      size="small"
                    />
                  )}
                  {locationAlertSummary.low > 0 && (
                    <Chip
                      icon={<InfoIcon />}
                      label={`${locationAlertSummary.low} Low`}
                      color="info"
                      size="small"
                    />
                  )}
                </Stack>
              </Paper>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Location Information
            </Typography>
            <Box mb={2}>
              <Typography variant="body1">
                <strong>Address:</strong> {locationDetails.address_line1}
                {locationDetails.address_line2 && <>, {locationDetails.address_line2}</>}
              </Typography>
              <Typography variant="body1">
                {locationDetails.city}, {locationDetails.state} {locationDetails.postal_code}
              </Typography>
              <Typography variant="body1">
                <strong>Type:</strong> {locationDetails.location_type}
              </Typography>
              {locationDetails.capacity && (
                <Typography variant="body1">
                  <strong>Capacity:</strong> {locationDetails.capacity}
                </Typography>
              )}
              {locationDetails.site_note && (
                <Typography variant="body1">
                  <strong>Site Notes:</strong> {locationDetails.site_note}
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Installations Summary
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Total Installations: {locationDetails.installation_count} | 
              Active: {locationDetails.active_installation_count} | 
              Inactive: {locationDetails.inactive_installation_count} | 
              Maintenance: {locationDetails.maintenance_installation_count}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Serial Number</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Manufacturer</TableCell>
                    <TableCell>Install Date</TableCell>
                    <TableCell>Warranty Expiry</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Alerts</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {locationDetails.installations_json && locationDetails.installations_json.map((installation) => {
                    const alerts = installationAlerts[installation.installation_id] || [];
                    const alertSummary = getAlertSummary(alerts);
                    return (
                      <TableRow key={installation.installation_id}>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => {
                              handleClose();
                              navigate(`/installations/${installation.installation_id}`);
                            }}
                            sx={{ textDecoration: 'none', color: 'primary.main' }}
                          >
                            {installation.serial_number}
                          </Link>
                        </TableCell>
                        <TableCell>{installation.model_name}</TableCell>
                        <TableCell>{installation.manufacturer}</TableCell>
                        <TableCell>
                          {installation.install_date ? format(new Date(installation.install_date), 'MMM d, yyyy') : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {installation.warranty_expiry_date ? format(new Date(installation.warranty_expiry_date), 'MMM d, yyyy') : 'N/A'}
                        </TableCell>
                        <TableCell>{installation.status}</TableCell>
                        <TableCell>
                          <Tooltip title={getAlertTooltip(alerts)}>
                            <Box>
                              <Chip
                                label={`${alerts.length} Alert${alerts.length !== 1 ? 's' : ''}`}
                                color={getAlertChipColor(alerts)}
                                size="small"
                                onClick={() => {
                                  handleClose();
                                  navigate(`/installations/${installation.installation_id}?tab=alerts`);
                                }}
                                sx={{ cursor: 'pointer' }}
                              />
                            </Box>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationDetailsDialog; 